import { Box, HStack, SystemStyleObject, Text } from '@chakra-ui/react';

import { relativeFormat } from '../../../../../utils/dates';
import { formatMoney, pluralize } from '../../../../../utils/helpers';

type OffersSummaryRowProps = {
  bestOffer: number | null;
  offerCount: number;
  listedAt?: string | null;
};

const showFrom = (minWidth: string): SystemStyleObject => ({
  display: 'none',
  [`@media screen and (min-width: ${minWidth})`]: { display: 'flex' },
});

const COUNT_MIN_WIDTH = '360px';
const LISTED_MIN_WIDTH = '520px';

const StatLabel = ({ children }: { children: string }) => (
  <Text as="span" fontSize="11.5px" color="grayLight" letterSpacing="0.01em">
    {children}
  </Text>
);

const StatValue = ({ children }: { children: string }) => (
  <Text
    as="span"
    fontFamily="heading"
    fontWeight="700"
    fontSize="14px"
    color="leaseEndBlue"
    letterSpacing="-0.003em"
  >
    {children}
  </Text>
);

const StatDivider = ({ sx }: { sx?: SystemStyleObject }) => (
  <Box w="1px" alignSelf="stretch" bg="grayMediumBackground" sx={sx} />
);

const OffersSummaryRow = ({ bestOffer, offerCount, listedAt }: OffersSummaryRowProps) => {
  const listed = listedAt != null && listedAt !== '' ? relativeFormat(listedAt) : '';
  const hasOffers = offerCount > 0;

  const listedSx = showFrom(LISTED_MIN_WIDTH);
  const countSx = showFrom(COUNT_MIN_WIDTH);

  return (
    <HStack spacing="14px" align="center" flexWrap="nowrap" justify="flex-end">
      {hasOffers ? (
        <>
          <HStack as="span" spacing="6px" align="baseline">
            <StatLabel>Best so far</StatLabel>
            <StatValue>{formatMoney(bestOffer)}</StatValue>
          </HStack>
          {listed !== '' && (
            <>
              <StatDivider sx={listedSx} />
              <HStack as="span" spacing="6px" align="baseline" sx={listedSx}>
                <StatLabel>Listed</StatLabel>
                <StatValue>{listed}</StatValue>
              </HStack>
            </>
          )}
          <StatDivider sx={countSx} />
          <HStack as="span" spacing="6px" align="baseline" sx={countSx}>
            <StatValue>{`${offerCount}`}</StatValue>
            <StatLabel>{`${pluralize('offer', offerCount)} in`}</StatLabel>
          </HStack>
        </>
      ) : (
        <>
          <HStack as="span" spacing="6px" align="baseline">
            <StatValue>0</StatValue>
            <StatLabel>active offers</StatLabel>
          </HStack>
          {listed !== '' && (
            <>
              <StatDivider sx={listedSx} />
              <HStack as="span" spacing="6px" align="baseline" sx={listedSx}>
                <StatLabel>Listed</StatLabel>
                <StatValue>{listed}</StatValue>
              </HStack>
            </>
          )}
        </>
      )}
    </HStack>
  );
};

export default OffersSummaryRow;
